import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ActivityTimeline = ({ activities, onAddActivity, currentUser, canEdit }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newActivity, setNewActivity] = useState('');
  const [activityType, setActivityType] = useState('comment');

  const getActivityIcon = (type) => {
    switch (type) {
      case 'created': return 'Plus';
      case 'status_change': return 'RefreshCw';
      case 'assignment': return 'UserPlus';
      case 'escalation': return 'AlertTriangle';
      case 'comment': return 'MessageSquare';
      case 'file_upload': return 'Paperclip';
      case 'system_update': return 'Settings';
      case 'resolution': return 'CheckCircle';
      default: return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'created': return 'text-success';
      case 'status_change': return 'text-primary';
      case 'assignment': return 'text-accent';
      case 'escalation': return 'text-warning';
      case 'comment': return 'text-muted-foreground';
      case 'file_upload': return 'text-secondary';
      case 'system_update': return 'text-muted-foreground';
      case 'resolution': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return date?.toLocaleString();
  };

  const handleAddActivity = (e) => {
    e?.preventDefault();
    if (newActivity?.trim()) {
      onAddActivity({
        id: Date.now(),
        type: activityType,
        description: newActivity?.trim(),
        user: currentUser?.name,
        userId: currentUser?.id,
        timestamp: new Date(),
        details: activityType === 'comment' ? { comment: newActivity?.trim() } : null
      });
      setNewActivity('');
      setShowAddForm(false);
    }
  };

  const renderActivityContent = (activity) => {
    switch (activity?.type) {
      case 'created':
        return `Created incident "${activity?.details?.title || 'Untitled'}"`;
      case 'status_change':
        return `Changed status from "${activity?.details?.from}" to "${activity?.details?.to}"`;
      case 'assignment':
        return `Assigned incident to ${activity?.details?.assignee}`;
      case 'escalation':
        return `Escalated incident to ${activity?.details?.level} level`;
      case 'comment':
        return activity?.details?.comment || activity?.description;
      case 'file_upload':
        return `Uploaded file: ${activity?.details?.fileName}`;
      case 'system_update':
        return `System automatically updated: ${activity?.details?.change}`;
      case 'resolution':
        return `Resolved incident: ${activity?.details?.resolution}`;
      default:
        return activity?.description;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Clock" size={20} />
          Activity Timeline ({activities?.length})
        </h2>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Add Note
          </Button>
        )}
      </div>
      {/* Add Activity Form */}
      {showAddForm && (
        <form onSubmit={handleAddActivity} className="mb-6 p-4 bg-muted/50 rounded-lg">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Activity Type
              </label>
              <select
                value={activityType}
                onChange={(e) => setActivityType(e?.target?.value)}
                className="w-full p-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="comment">Comment</option>
                <option value="status_change">Status Change</option>
                <option value="assignment">Assignment</option>
                <option value="escalation">Escalation</option>
                <option value="system_update">System Update</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description
              </label>
              <textarea
                value={newActivity}
                onChange={(e) => setNewActivity(e?.target?.value)}
                placeholder="Enter activity description..."
                className="w-full p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                rows={3}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                variant="default"
                size="sm"
                iconName="Check"
              >
                Add Activity
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconName="X"
                onClick={() => {
                  setShowAddForm(false);
                  setNewActivity('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      )}
      {/* Timeline */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

        <div className="space-y-6">
          {activities?.map((activity, index) => (
            <div key={activity?.id} className="relative flex gap-4">
              {/* Timeline Node */}
              <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 border-card bg-card ${getActivityColor(activity?.type)}`}>
                <Icon name={getActivityIcon(activity?.type)} size={20} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pb-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Image
                      src={`https://api.dicebear.com/7.x/initials/svg?seed=${activity?.user}`}
                      alt={activity?.user}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="font-medium text-foreground">{activity?.user}</span>
                    <span className="text-sm text-muted-foreground">
                      {formatTimestamp(activity?.timestamp)}
                    </span>
                  </div>
                </div>

                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-sm text-foreground">
                    {renderActivityContent(activity)}
                  </p>
                  
                  {/* Additional Details */}
                  {activity?.details && activity?.type !== 'comment' && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <div className="text-xs text-muted-foreground space-y-1">
                        {Object.entries(activity?.details)?.map(([key, value]) => (
                          <div key={key}>
                            <span className="capitalize">{key?.replace('_', ' ')}:</span>
                            <span className="ml-1 font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {activities?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Clock" size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No activities yet</p>
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;